import React, { useState, useRef, useEffect } from 'react'
import { Button, Col, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, FormGroup, Input, InputGroup, InputGroupText, Label, Row } from 'reactstrap'
import debounce from 'debounce'
import { X } from 'react-feather'

export default function AdminLinkLibraryItem({ libraryItem }) {
  const [isFocused, setIsFocused] = useState(false)
  const [currentItem, setCurrentItem] = useState(libraryItem)
  const [index, setIndex] = useState(0)
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const inputRef = useRef()
  const fetchControllerRef = useRef(null)
  const resultsRef = useRef(results.length)
  const indexRef = useRef(index)

  const updateIndex = (e) => {
    if (e.keyCode == 38) {
      e.preventDefault()
      setIndex(Math.max(indexRef.current - 1, 0))
    } else if (e.keyCode == 40) {
      e.preventDefault()
      setIndex(Math.min(indexRef.current + 1, resultsRef.current.length - 1))
    } else if (e.keyCode == 13) {
      e.preventDefault()
      handleItemSelection(resultsRef.current[indexRef.current])
    }
  }

  useEffect(() => {
    resultsRef.current = results
    indexRef.current = index
  }, [results, index])

  useEffect(() => {
    const openDropdown = () => setIsFocused(true)
    // const closeDropdown = () => setTimeout(() => setIsFocused(false), 1000)

    if (inputRef.current) {
      // inputRef.current.addEventListener('focus', openDropdown)
      // inputRef.current.addEventListener('blur', closeDropdown)
      inputRef.current.addEventListener('keydown', updateIndex)
    }

    return () => {
      if (inputRef.current) {
        // inputRef.current.removeEventListener('focus', openDropdown)
        // inputRef.current.removeEventListener('blur', closeDropdown)
        inputRef.current.removeEventListener('keydown', updateIndex)
      }
    }
  }, [])

  const updateResults = () => {
    const value = inputRef.current.value
    setIsFocused(true)

    if (value.length > 0) {
      setLoading(true)
      fetchNewResults(value)
    } else {
      setLoading(false)
    }
  }

  const fetchNewResults = async (value) => {
    // If there's a pending request, abort it
    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort()
    }

    // Create a new controller for the new request
    const controller = new AbortController()
    fetchControllerRef.current = controller

    try {
      const response = await fetch(`/admin/library/search.json?query=${encodeURIComponent(value)}`, {
        signal: controller.signal,
      })
      if (!response.ok) throw new Error('Network error')
      const data = await response.json()
      setResults(data)
    } catch (err) {
      if (err.name === 'AbortError') {
        // Fetch was canceled: ignore
      } else {
        // Handle other errors if needed
        setResults([])
      }
    }

    setLoading(false)
  }

  const handleItemSelection = (result) => {
    inputRef.current.value = result.name
    inputRef.current.blur()
    setCurrentItem(result)
  }

  const handleClearCurrentItem = () => {
    inputRef.current.focus()
    setCurrentItem(null)
  }

  const debouncedUpdateResults = debounce(updateResults, 300)

  const isOpen = !currentItem && isFocused && (loading || inputRef.current.value.length >= 3)

  return (
    <FormGroup>
      <Label>Name</Label>
      <Dropdown toggle={() => { setIsFocused(!isFocused) }} isOpen={isOpen}>
        <DropdownToggle tag="div">
          <InputGroup>
            {currentItem && (
              <InputGroupText>
                <img src={currentItem.preview_url} alt={currentItem.name} className="select-field-preview-image" />
              </InputGroupText>
            )}
            <Input
              innerRef={inputRef}
              id="org-name"
              name="org[name]"
              bsSize="lg"
              placeholder="Org Name"
              onChange={debouncedUpdateResults}
              autoComplete="organization-name" />
            {currentItem && (
              <Button color="secondary bg-gray" onClick={handleClearCurrentItem}>
                <X size={16} className="icon-valign text-dark" />
              </Button>
            )}
          </InputGroup>
        </DropdownToggle>
        <DropdownMenu className="w-100">
          { loading ? (
            <DropdownItem disabled={true}>Loading…</DropdownItem>
          ) : (
            results.length > 0 ? (
              results.map((result, idx) => (
                <DropdownItem key={result.id} className={idx == index ? 'bg-gray' : ''} onClick={() => handleItemSelection(result)}>
                  <Row className="align-items-center">
                    <Col xs="auto" className="pe-0">
                      <img src={result.preview_url} alt={result.name} className="select-field-preview-image" />
                    </Col>
                    <Col>
                      <strong style={{ color: result.color }}>{result.name}</strong>
                    </Col>
                    <Col xs="auto">
                      {result.mascot}
                    </Col>
                  </Row>
                </DropdownItem>
              ))
            ) : (
              <DropdownItem disabled={true}>No results found</DropdownItem>
            )
          )}
        </DropdownMenu>
      </Dropdown>
      {currentItem && <Input type="hidden" name="org[library_item_id]" value={currentItem.id} />}
    </FormGroup>
  )
}
